import { useNavigate, useParams } from "react-router-dom";
import {
  deliveryAndTransferValidationSchema,
  validationSchema,
  validationSchemaKeeperOptional,
  newAddressViewValidationSchema,
  newAddressViewValidationSchemaKeeperOptional,
} from "./validationSchema";
import { MultiStepProgress } from "components/MultiStepProgress";
import { FormInput } from "components/basic/FormInput";
import { FormSelect } from "components/basic/FormSelect";
import { Address } from "components/Address/Address";
import { useFormik } from "formik";
import { ButtonRound } from "components/basic/Buttons";
import { CustomisePlate } from "@/components/CustomisePlate/CustomisePlate";
import { Collapsible } from "@/components/basic/Collapsible";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  getDeliveryPrices,
  getRegistrationDetails,
  getSalesFormMessage,
  initializeFormId,
  recoverFormValues,
  setFormValues,
  selectDeliveryPrices,
  selectFormValues,
  selectRegistrationDetails,
  selectSalesFormMessage,
  selectVehicleOptions,
  storeCustomiseValues,
} from "@/pages/CheckoutInfo/checkoutInfoSlice";
import { CheckoutCardRadios } from "@/components/CheckoutCardRadios/CheckoutCardRadios";
import {
  extraRadios,
  transferRadiosFactory,
  deliveryRadiosFactory,
} from "./constants";
import { PurchaserDetails } from "@/components/CustomiseForm/PurchaserDetails/PurchaserDetails";
import { selectIsAuthenticate, selectUserInfo } from "@/core/store/authSlice";
import { trackAddToCart, trackBeginCheckout, trackCheckoutValidationBlocked } from "@/core/helpers/tracking";
import { progressInfosFactory } from "../constants";
import { CHECKOUT_CONFIG } from "../config";
import v5cnamearea from "@/assets/img/checkout/v5cnamearea.jpg";
import { Icon } from "@rmwc/icon";
import Switch from "@/components/basic/Switch/Switch";

const CURRENT_PAGE_NUMBER = 1;
// Keeper/nominee deferral ("I don't have my V5C to hand") — temporarily withdrawn.
// Flip to true to restore the skip checkbox and the optional-keeper validation path.
// Downstream chase machinery (keeper_form.php, initKeeperChase, crm.keeper_chase,
// chase emails, nr-internal AWAITING badge) is untouched and still serves the
// existing backlog plus legacy/internal flows that land without a keeper.
const KEEPER_DEFERRAL_ENABLED = false;

const CheckoutCustomise = () => {
  const cardClassName = "md:p-6 p-5 bg-card-bg-grey rounded-2xl";
  const hadTryToSubmit = useRef(false);
  const prevSubmitCountRef = useRef(0);

  const [isFinance, setFinance] = useState(false);
  const [isZeroPercentFinance, setIsZeroPercentFinance] = useState(false);
  const [keeperDeferred, setKeeperDeferred] = useState(false);
  const newAddressView = true;

  // DB-driven checkout offer — plates_policy / transfer_policy: 'optional' | 'forced' | 'excluded'
  const [offerConfig, setOfferConfig] = useState<{
    plates_policy: string;
    transfer_policy: string;
    postage_policy: string;
    auto_discount_code: string | null;
    display_label: string | null;
  } | null>(null);

  const { registration: urlRegistration } = useParams();
  const registration = useMemo(() => {
    return urlRegistration?.replace(/\+/g, " ");
  }, [urlRegistration]);

  const progressInfos = useMemo(
    () => progressInfosFactory({ registration: registration || "" }),
    [registration]
  );
  const progressTitles = useMemo(
    () => progressInfos.map((info) => info.title),
    [progressInfos]
  );

  const navigate = useNavigate();
  const [thirdPartyQualify, setThirdPartyQualify] = useState(false);
  const [sendCommunciation, setSendCommunciation] = useState(false);

  const nextPage = () => {
    let url = progressInfos[CURRENT_PAGE_NUMBER + 1].path;
    if (isFinance) {
      url = url + "?finance=true";
    }
    // Use hard navigation to avoid Formik re-initialisation (enableReinitialize)
    // racing with React Router's client-side navigate when storeCustomiseValues.fulfilled
    // updates Redux state immediately after continueCallback fires.
    window.location.href = url;
  };

  const prevPage = () => {
    navigate(progressInfos[CURRENT_PAGE_NUMBER - 1].path);
  };

  const dispatch = useAppDispatch();
  const vehicleYearOptionsString = useAppSelector(selectVehicleOptions);
  const registrationDetails = useAppSelector(selectRegistrationDetails);
  const deliveryPrices = useAppSelector(selectDeliveryPrices);
  const storeFormValues = useAppSelector(selectFormValues);
  const salesFormMessage = useAppSelector(selectSalesFormMessage);
  const userInfo = useAppSelector(selectUserInfo);
  const isAuthenticate = useAppSelector(selectIsAuthenticate);

  // We need to move these effects before formik initialization but not the cache effect
  useEffect(() => {
    dispatch(initializeFormId());
    const urlParams = new URLSearchParams(window.location.search);
    setFinance(urlParams.get("finance") === "true");
    try {
      const saved = sessionStorage.getItem("selectedFinanceDetails");
      const parsed = saved ? JSON.parse(saved) : {};
      setIsZeroPercentFinance(parsed.isZeroPercent === true);
    } catch (e) {
      setIsZeroPercentFinance(false);
    }
    document.title =
      "Buy " + registration + " Private Number Plate from New Reg";
  }, [dispatch, registration]);

  // Load DB-driven offer config from sessionStorage (set by CheckoutInfo on mount)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('nrCheckoutOffer');
      if (raw) {
        setOfferConfig(JSON.parse(raw));
      }
    } catch (e) {
      // No offer config — all policies default to 'optional'
    }
  }, []);

  // Enforce plates_policy and transfer_policy from DB offer
  useEffect(() => {
    if (!offerConfig) return;
    // Plates
    if (offerConfig.plates_policy === 'forced') {
      setFieldValue("order_plate", "yes");
      dispatch(setFormValues({ order_plate: "yes" }));
    } else if (offerConfig.plates_policy === 'excluded') {
      setFieldValue("order_plate", "no");
      dispatch(setFormValues({ order_plate: "no" }));
    }
    // Transfer service (only relevant when existDeliveryAndTransfer is true)
    if (offerConfig.transfer_policy === 'forced') {
      setFieldValue("transfer_type", "standard");
      dispatch(setFormValues({ transfer_type: "standard" }));
    } else if (offerConfig.transfer_policy === 'excluded') {
      setFieldValue("transfer_type", "none");
      dispatch(setFormValues({ transfer_type: "none" }));
    }
  }, [offerConfig]);

  // Legacy static config — force plates for 0% finance orders (controlled by CHECKOUT_CONFIG)
  useEffect(() => {
    if (isZeroPercentFinance && CHECKOUT_CONFIG.includeAcrylicsOnZeroPercentFinance) {
      setFieldValue("order_plate", "yes");
      dispatch(setFormValues({ order_plate: "yes" }));
    }
  }, [isZeroPercentFinance]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  useEffect(() => {
    if (storeFormValues?.formId && !storeFormValues?.year) {
      dispatch(
        recoverFormValues({
          id: storeFormValues?.formId,
        })
      );
    }
  }, [storeFormValues?.formId, storeFormValues?.year, dispatch]);

  const sourcecode = registrationDetails?.sourcecode;
  const existDeliveryAndTransfer = useMemo(() => {
    return sourcecode == "1";
  }, [sourcecode]);

  const formValidationSchema = useMemo(() => {
    const baseSchema = ((KEEPER_DEFERRAL_ENABLED && keeperDeferred)
      ? (newAddressView ? newAddressViewValidationSchemaKeeperOptional : validationSchemaKeeperOptional)
      : (newAddressView ? newAddressViewValidationSchema : validationSchema)) as any;
    if (existDeliveryAndTransfer) {
      return baseSchema.concat(deliveryAndTransferValidationSchema);
    } else {
      return baseSchema;
    }
  }, [existDeliveryAndTransfer, newAddressView, keeperDeferred]);

  const initialValues = useMemo(() => {
    return Object.assign(
      {
        email: (storeFormValues?.email || (userInfo?.user as string) || ""),
        title: (storeFormValues?.title || (userInfo?.title as string) || ""),
        surname: (storeFormValues?.surname || (userInfo?.surname as string) || ""),
        forename: (storeFormValues?.forename || (userInfo?.forename as string) || ""),
        telephone1: (storeFormValues?.telephone1 || (userInfo?.telephone1 as string) || ""),
        address1: (storeFormValues?.address1 || (userInfo?.address1 as string) || ""),
        address2: (storeFormValues?.address2 || (userInfo?.address2 as string) || ""),
        address3: (storeFormValues?.address3 || (userInfo?.address3 as string) || ""),
        address4: (storeFormValues?.address4 || (userInfo?.address4 as string) || ""),
        postcode: (storeFormValues?.postcode || (userInfo?.postcode ? (userInfo.postcode as string).toUpperCase() : "") || ""),
        order_plate: storeFormValues?.order_plate || "no",
        year: storeFormValues?.year || "",
        vehicle_keeper: storeFormValues?.vehicle_keeper || "",
        gift_wrapping: storeFormValues?.gift_wrapping || "no",
        transfer_type: storeFormValues?.transfer_type || "",
        delivery_type: storeFormValues?.delivery_type || "",
        isManualAddress: !!(storeFormValues?.address1 || userInfo?.address1),
        
        // Additional address fields for finance applications
        ...(newAddressView ? {
          flatNumber: storeFormValues?.flatNumber || "",
          buildingName: storeFormValues?.buildingName || "",
          buildingNumber: storeFormValues?.buildingNumber || (() => {
            if (!userInfo?.address1) return "";
            const p = (userInfo.address1 as string).split(" ");
            return p.length > 0 && !isNaN(Number(p[0])) ? p[0] : "";
          })(),
          street: storeFormValues?.street || (() => {
            if (!userInfo?.address1) return "";
            const p = (userInfo.address1 as string).split(" ");
            const hasNum = p.length > 0 && !isNaN(Number(p[0]));
            return p.slice(hasNum ? 1 : 0).join(" ");
          })(),
          locality: storeFormValues?.locality || (userInfo?.address2 as string) || "",
          townOrCity: storeFormValues?.townOrCity || (userInfo?.address3 as string) || "",
          county: storeFormValues?.county || (userInfo?.address4 as string) || ""
        } : {})
      },
      storeFormValues?.order_plate === "yes"
        ? {
            digit_style: storeFormValues?.digit_style,
            acrylic_plate_font: storeFormValues?.acrylic_plate_font,
            euro: storeFormValues?.euro,
            acrylic_plate_background: storeFormValues?.acrylic_plate_background,
            border: storeFormValues?.border,
            acrylic_plate_border: storeFormValues?.acrylic_plate_border,
            plate_size: storeFormValues?.plate_size,
            acrylic_plate_size: storeFormValues?.acrylic_plate_size,
            acrylic_plate_euro: storeFormValues?.acrylic_plate_euro,
          }
        : {}
    );
  }, [storeFormValues, newAddressView, userInfo]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: formValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      const formId = storeFormValues?.formId;
      const data: { [key: string]: any } = {
        ...values,
        sourcecode,
        display_date1: registrationDetails?.display_date1,
        corr_or_garage: "Correspond With Me",
        registration,
        formId,
      };
      delete data.isManualAddress;
      if (!existDeliveryAndTransfer) {
        delete data.transfer_type;
        delete data.delivery_type;
      }
      if (data.order_plate === "no") {
        data.gift_wrapping = "no";
      }
      if (data.gift_wrapping === "yes") {
        data.gift_wrapping_toggle = "yes";
      }
      dispatch(
        storeCustomiseValues({
          data,
          continueCallback() {
            setSubmitting(false);
            const _atcPrice = parseFloat((registrationDetails as any)?.price || (registrationDetails as any)?.display_price || '0');
            trackAddToCart(registration || "", _atcPrice);
            sessionStorage.setItem('nrThirdParty', thirdPartyQualify ? '1' : '0');
            sessionStorage.setItem('nrPlainEnvelope', sendCommunciation ? '1' : '0');
            const isKeeperDeferred = keeperDeferred || !values.year || !values.vehicle_keeper;
            sessionStorage.setItem('nrKeeperDeferred', isKeeperDeferred ? '1' : '0');
            nextPage();
          },
        })
      ).catch(() => {
        // Re-enable the Next button if the API call fails so the user can retry.
        setSubmitting(false);
      });
    },
  });
  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    isSubmitting,
    submitCount,
  } = formik;

  // Dual-run capture — fire-and-forget when we have a valid email + registration
  // captureKey includes auth state so login at checkout re-fires with oracle_user_id
  // 1500ms debounce prevents partial/mid-typing emails from being captured
  const _capturedEmailRef = useRef("");
  const _trackedBeginCheckoutRef = useRef(""); // separate guard — keyed on reg+email only, not auth state
  useEffect(() => {
    // Fall back to userInfo.user so capture fires even if Formik hasn't processed
    // setFieldValue("email") yet (race between two effects in the same render cycle)
    const email = values.email || (userInfo?.user as string) || "";
    if (!registration || !email) return;
    // {2,} requires at least 2 chars after the last dot — filters single-char partial TLDs
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return;
    const captureKey = registration + ":" + email + ":" + String(isAuthenticate);
    if (_capturedEmailRef.current === captureKey) return;
    _capturedEmailRef.current = captureKey; // lock before async work to prevent double-fire on re-renders

    // Fire begin_checkout once per registration+email — keyed separately from captureKey so
    // auth state changes (isAuthenticate false→true) don't re-fire the GA4/nr-track event.
    const _bcKey = registration + ":" + email;
    if (_trackedBeginCheckoutRef.current !== _bcKey) {
      _trackedBeginCheckoutRef.current = _bcKey;
      const _bcPrice = parseFloat((registrationDetails as any)?.price || (registrationDetails as any)?.display_price || "0");
      trackBeginCheckout(registration, _bcPrice, email);
    }

    const fullName = [values.forename, values.surname].filter(Boolean).join(" ");
    const body = new URLSearchParams({
      action:           "capture_checkout",
      registration:     registration,
      email:            email,
      oracle_user_id:   userInfo?.user_cust_id || "",
      is_authenticated: isAuthenticate ? "true" : "false",
      step:             "2",
      name:             fullName,
      phone:            values.telephone1 || "",
    });
    const timer = setTimeout(() => {
      fetch("/ajax.php", {
        method:    "POST",
        headers:   { "Content-Type": "application/x-www-form-urlencoded" },
        body:      body.toString(),
        keepalive: true,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [values.email, registration, userInfo, isAuthenticate]);

  useEffect(() => {
    if (submitCount === 0 || Object.keys(errors).length === 0) return;
    if (submitCount === prevSubmitCountRef.current) return;
    prevSubmitCountRef.current = submitCount;
    const form = (document.getElementById("root")?.querySelector("form") ?? null) as HTMLFormElement | null;
    if (!form) return;
    const firstErrorField = Array.from(form.querySelectorAll("[name]")).find(
      (el) => errors[el.getAttribute("name") as keyof typeof errors]
    ) as HTMLElement | undefined;
    if (firstErrorField) {
      const rect = firstErrorField.getBoundingClientRect();
      let target: HTMLElement = firstErrorField;
      if (rect.height <= 1 || rect.width <= 1) {
        // sr-only input (1px): walk up to find the ancestor containing the error alert
        let el: HTMLElement | null = firstErrorField.parentElement;
        while (el && el !== form) {
          if (el.querySelector('[role="alert"]')) { target = el; break; }
          el = el.parentElement;
        }
        if (target === firstErrorField) {
          target = (firstErrorField.parentElement as HTMLElement) || firstErrorField;
        }
      }
      // scrollIntoView is the most reliable cross-platform approach.
      // window.scrollTo with getBoundingClientRect breaks on iOS Safari (dynamic address bar).
      target.scrollIntoView({ block: 'center' });
    }
  }, [submitCount, errors]);

  // Dual-run capture — log blocked Next-clicks (validation errors) for checkout funnel analysis
  useEffect(() => {
    if (submitCount === 0 || Object.keys(errors).length === 0) return;
    if (!registration) return;
    const errorFields = Object.keys(errors).join(',');
    const body = new URLSearchParams({
      action:           'capture_checkout_attempt',
      registration:     registration,
      email:            values.email || '',
      oracle_user_id:   userInfo?.user_cust_id || '',
      error_fields:     errorFields,
      submit_count:     String(submitCount),
    });
    fetch('/ajax.php', {
      method:    'POST',
      headers:   { 'Content-Type': 'application/x-www-form-urlencoded' },
      body:      body.toString(),
      keepalive: true,
    }).catch(() => {});
    trackCheckoutValidationBlocked(registration, errorFields);
  }, [submitCount]);

  // Get form data from sessionStorage when page loads (moved here to have access to setFieldValue)
  useEffect(() => {
    const cacheKey = registration ? `form_${registration}` : '';
    if (cacheKey) {
      try {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);

          // Restore form data to initialValues
          if (parsedData && Object.keys(parsedData).length > 0) {
            Object.keys(parsedData).forEach(key => {
              if (parsedData[key] !== undefined && parsedData[key] !== null) {
                setFieldValue(key, parsedData[key]);
              }
            });
            
            // If address info exists, set isManualAddress to true
            if (parsedData.address1 || (newAddressView && parsedData.street)) {
              setFieldValue('isManualAddress', true);
            }
          }
        }
      } catch (e) {
        console.error('Error reading cache:', e);
      }
    }
  }, [registration, newAddressView, setFieldValue]);

  // Save form values to sessionStorage when values change
  useEffect(() => {
    if (!registration) return;
    
    const cacheKey = `form_${registration}`;
    const saveFormData = () => {
      try {
        // Filter out undefined and null values
        const dataToSave: Record<string, any> = {};
        Object.keys(values).forEach(key => {
          if (values[key as keyof typeof values] !== undefined && 
              values[key as keyof typeof values] !== null) {
            dataToSave[key] = values[key as keyof typeof values];
          }
        });
        
        // Add timestamp for debugging
        dataToSave['_timestamp'] = new Date().toISOString();
        
        sessionStorage.setItem(cacheKey, JSON.stringify(dataToSave));
      } catch (e) {
        console.error('Error saving to cache:', e);
      }
    };
    
    // Use debounce, only save after user stops typing for a while
    const timerId = setTimeout(saveFormData, 500);
    
    return () => clearTimeout(timerId);
  }, [values, registration]);

  const isCustomPlate = useMemo(
    () => values.order_plate === "yes",
    [values.order_plate]
  );

  const transferRadios = transferRadiosFactory({ registration });
  const deliveryRadios = !!deliveryPrices
    ? deliveryRadiosFactory({ ...deliveryPrices, hasPlates: isCustomPlate })
    : [];

  useEffect(() => {
    dispatch(getDeliveryPrices());
  }, [dispatch]);

  useEffect(() => {
    if (registration) {
      dispatch(getSalesFormMessage({ registration }));
    }
  }, [registration, dispatch]);

  useEffect(() => {
    if (!vehicleYearOptionsString && registration) {
      dispatch(getRegistrationDetails({ registration }));
    }
  }, [vehicleYearOptionsString, registration, dispatch]);

  const vehicleYearOptions = useMemo(() => {
    if (vehicleYearOptionsString) {
      return vehicleYearOptionsString
        .split(",")
        .filter((value) => value !== "Please Select ")
        .filter((value) => !!value.replace(/(^\s*)/, ""))
        .map((value) => value.replace(/\./g, "/"))
        .map((value) => {
          const matchDate = value.match(
            /\d{1,2}\/\d{1,2}\/\d{1,2}\s*-\s*\d{1,2}\/\d{1,2}\/\d{1,2}/
          );
          if (matchDate) {
            const date = matchDate[0];
            return value.replace(date, `(${date})`);
          } else {
            return value;
          }
        });
    }
    return [];
  }, [vehicleYearOptionsString]);

  const shouldFillFormUseUserInfo = useRef(true);
  useEffect(() => {
    if (shouldFillFormUseUserInfo.current) {
      if (values.postcode) {
        shouldFillFormUseUserInfo.current = false;
      }
    }
  }, [values.postcode]);

  // useEffect(() => {
  //   const beforeUnloadEvent = (event: { returnValue: string }) =>
  //     (event.returnValue = "Leave Site?");
  //   window.addEventListener("beforeunload", beforeUnloadEvent);
  //   return () => {
  //     window.removeEventListener("beforeunload", beforeUnloadEvent);
  //   };
  // }, []);

  useEffect(() => {
    if (userInfo) {
      // Email is always synced — field is disabled when authenticated so user can't edit it
      setFieldValue("email", userInfo.user || "");
      setFieldTouched("email", false);

      if (shouldFillFormUseUserInfo.current) {
      [
        "email",
        "title",
        "surname",
        "forename",
        "telephone1",
        "address1",
        "address2",
        "address3",
        "address4",
        // "flatNumber",
        // "buildingName",
        // "buildingNumber",
        // "street",
        // "locality",
        // "townOrCity",
        // "county",
        "postcode",
      ].forEach((key) => {
        let infoKey = key;
        switch (key) {
          case "email":
            infoKey = "user";
            break;
          default:
            break;
        }
        let infoValue = userInfo[infoKey] || "";
        switch (key) {
          case "postcode":
            infoValue = infoValue.toUpperCase();
            break;
          default:
            break;
        }
        setFieldValue(key, infoValue);
      });
      setFieldValue("isManualAddress", true);
      
      // If we have address1 data, try to parse it
      if (newAddressView && userInfo.address1) {
        const addressParts = userInfo.address1.split(" ");
        const buildingNumber = addressParts.length > 0 && !isNaN(Number(addressParts[0])) 
          ? addressParts[0] 
          : "";
        const street = addressParts.length > 0 
          ? addressParts.slice(buildingNumber ? 1 : 0).join(" ") 
          : userInfo.address1 || "";
          
        setFieldValue("buildingNumber", buildingNumber);
        setFieldValue("street", street);
        setFieldValue("locality", userInfo.address2 || "");
        setFieldValue("townOrCity", userInfo.address3 || "");
        setFieldValue("county", userInfo.address4 || "");
      }
      // Clear touched so populated fields don't show stale errors
      ["email", "title", "surname", "forename", "telephone1",
       "address1", "address2", "address3", "address4", "postcode"
      ].forEach((key) => setFieldTouched(key, false));
      } // end shouldFillFormUseUserInfo guard
    }
  }, [userInfo, newAddressView, setFieldValue, setFieldTouched]);

  return (
    <div>
      <MultiStepProgress
        stepNames={progressTitles}
        page={CURRENT_PAGE_NUMBER}
        onStepClick={(i) => {
          if (i < CURRENT_PAGE_NUMBER) {
            navigate(progressInfos[i].path);
          } else {
            // Mirror the Next Page button: set hadTryToSubmit so delivery/transfer
            // radio errors render, then submit to trigger validation + scroll-to-error.
            hadTryToSubmit.current = true;
            formik.submitForm();
          }
        }}
      />
      <form
        onSubmit={formik.handleSubmit}
        className="max-w-screen-sm w-11/12 flex-column mx-auto py-16"
      >
        <div>
          <h2 className="font-bold text-xl mb-6 text-left">
            Purchaser's Details
          </h2>
          <div className={`${cardClassName} pb-8 grid gap-4`}>
            <PurchaserDetails
              values={values}
              errors={errors}
              touched={touched}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              handleBlur={handleBlur}
            ></PurchaserDetails>
          </div>
          <div>
            <h2 className="font-bold text-xl mt-8 mb-3 text-left">
              Address Details
            </h2>
            <div className={`${cardClassName} grid gap-4`}>
              <Address
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                handleBlur={handleBlur}
                selectErrorMsg={
                  touched?.isManualAddress ? errors?.isManualAddress : ""
                }
                newAddressView={newAddressView}
              ></Address>
            </div>
          </div>
          <div>
            <h2 className="font-bold text-xl mt-8 mb-3 text-left">
              Order number plates for your vehicle here:
            </h2>
            {/* DB-driven forced plates policy */}
            {offerConfig?.plates_policy === 'forced' ? (
              <div className="bg-lime-50 border border-lime-400 rounded p-4 text-sm text-left">
                <p className="font-bold text-lime-700">✓ Physical plates are included with this offer.</p>
                <p className="text-gray-500 mt-1 text-xs">Plates cannot be removed from this order.</p>
              </div>
            ) : offerConfig?.plates_policy === 'excluded' ? (
              <div className="bg-gray-50 border border-gray-200 rounded p-4 text-sm text-left">
                <p className="text-gray-600">Physical plates are not available with this offer.</p>
              </div>
            ) : isZeroPercentFinance && CHECKOUT_CONFIG.includeAcrylicsOnZeroPercentFinance ? (
              /* Legacy static config for 0% finance */
              <div className="bg-lime-50 border border-lime-400 rounded p-4 text-sm text-left">
                <p className="font-bold text-lime-700">✓ Standard acrylic plates are included with your 0% finance order.</p>
                <p className="text-gray-500 mt-1 text-xs">Physical plates cannot be removed from 0% finance orders.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <button
                  className="h-12 rounded-xl font-semibold text-sm w-full transition-all"
                  style={!isCustomPlate ? {
                    border: "2px solid #358639",
                    backgroundColor: "#f0f9f1",
                    boxShadow: "0 4px 12px rgba(53,134,57,0.12)",
                    color: "#358639",
                  } : {
                    border: "2px solid #d1d5db",
                    backgroundColor: "#ffffff",
                    color: "#111827",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    setFieldValue("order_plate", "no");
                  }}
                >
                  {!isCustomPlate && <span className="mr-1">✓</span>} No
                </button>
                <button
                  className="h-12 rounded-xl font-semibold text-sm w-full transition-all"
                  style={isCustomPlate ? {
                    border: "2px solid #358639",
                    backgroundColor: "#f0f9f1",
                    boxShadow: "0 4px 12px rgba(53,134,57,0.12)",
                    color: "#358639",
                  } : {
                    border: "2px solid #d1d5db",
                    backgroundColor: "#ffffff",
                    color: "#111827",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    setFieldValue("order_plate", "yes");
                  }}
                >
                  {isCustomPlate && <span className="mr-1">✓</span>}
                  Yes
                </button>
              </div>
            )}
            <div
              className={`${cardClassName} mt-8 ${
                isCustomPlate ? "" : "hidden"
              }`}
            >
              <CustomisePlate
                isCustomPlate={isCustomPlate}
                values={values}
                setFieldValue={setFieldValue}
                registration={registration || ""}
              ></CustomisePlate>
            </div>
          </div>
        </div>
        {isCustomPlate && (
          <div>
            <h2 className="font-bold text-xl mt-8 mb-3 text-left">Extras</h2>
            <div
              className="flex px-4 py-6 bg-card-bg-grey rounded cursor-pointer"
              onClick={() => {
                setFieldValue(
                  "gift_wrapping",
                  values.gift_wrapping !== "yes" ? "yes" : "no"
                );
              }}
            >
              <div className="mx-16 flex justify-center align-center text-3xl">
                <Icon icon={extraRadios[0].icon} className="text-3xl" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-lg">{extraRadios[0].title}</h3>
                <h3 className="text-lg">{extraRadios[0].price}</h3>
                <p
                  className="text-xs font-light"
                  dangerouslySetInnerHTML={{
                    __html: extraRadios[0].description,
                  }}
                ></p>
              </div>
              <div className="switch-wrapper">
                <Switch
                  checked={values.gift_wrapping === "yes"}
                  onChange={(evt) =>
                    setFieldValue(
                      "gift_wrapping",
                      evt.currentTarget.checked ? "yes" : "no"
                    )
                  }
                  label=" "
                />
              </div>
            </div>
          </div>
        )}
        <div>
          <h2 className="font-bold text-xl mt-8 mb-3 text-left">Vehicle Details</h2>
          <div className={`${cardClassName} grid gap-4`}>
            <div style={{ opacity: keeperDeferred ? 0.4 : 1, pointerEvents: keeperDeferred ? 'none' : 'auto' }}>
              <FormSelect
                title="Please select the age of the receiving vehicle from the drop down menu:"
                name="year"
                label="Select vehicle year"
                options={vehicleYearOptions}
                onChange={(e) => {
                  const target = e.target as typeof e.target & {
                    value: string;
                  };
                  setFieldValue("year", target.value);
                }}
                onBlur={handleBlur}
                wrapperClassName="sm:block hidden"
                value={values.year}
                errorMsg={keeperDeferred ? "" : ((touched.year || submitCount > 0) ? errors.year : "")}
                valid={!keeperDeferred && !!touched.year && !errors.year && !!values.year}
              />
              <FormSelect
                title="Please select the age of the receiving vehicle from the drop down menu:"
                name="year"
                label="Select year"
                options={vehicleYearOptions}
                onChange={(e) => {
                  const target = e.target as typeof e.target & {
                    value: string;
                  };
                  setFieldValue("year", target.value);
                }}
                onBlur={handleBlur}
                wrapperClassName="sm:hidden block"
                value={values.year}
                errorMsg={keeperDeferred ? "" : ((touched.year || submitCount > 0) ? errors.year : "")}
                valid={!keeperDeferred && !!touched.year && !errors.year && !!values.year}
              />
              <p className="text-left mt-4">
                <b>
                  What is the name and/or company name that appears on the V5C UK
                  Registration Certificate?
                </b>{" "}
                If you are in any doubt, please refer to the V5C.
              </p>
              <FormInput
                name="vehicle_keeper"
                wrapperClassName="w-full"
                label="Registered Keeper"
                placeholder="Registered Keeper"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.vehicle_keeper}
                errorMsg={keeperDeferred ? "" : ((touched.vehicle_keeper || submitCount > 0) ? errors.vehicle_keeper : "")}
                valid={!keeperDeferred && !!touched.vehicle_keeper && !errors.vehicle_keeper && !!values.vehicle_keeper}
              />
            </div>
            {KEEPER_DEFERRAL_ENABLED && (
            <div
              className="mt-4 rounded-xl transition-all select-none"
              role="checkbox"
              aria-checked={keeperDeferred}
              style={{
                border: `2px solid ${keeperDeferred ? '#358639' : '#d1d5db'}`,
                backgroundColor: keeperDeferred ? '#f0f9f1' : '#ffffff',
                boxShadow: keeperDeferred ? '0 4px 12px rgba(53,134,57,0.12)' : '0 1px 2px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}
              onClick={() => {
                const next = !keeperDeferred;
                setKeeperDeferred(next);
                if (next) {
                  setFieldValue('year', '');
                  setFieldValue('vehicle_keeper', '');
                  setFieldTouched('year', false);
                  setFieldTouched('vehicle_keeper', false);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  const next = !keeperDeferred;
                  setKeeperDeferred(next);
                  if (next) {
                    setFieldValue('year', '');
                    setFieldValue('vehicle_keeper', '');
                    setFieldTouched('year', false);
                    setFieldTouched('vehicle_keeper', false);
                  }
                }
              }}
            >
              <div className="relative flex items-start gap-4 p-5 pr-14">
                <div
                  className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundColor: keeperDeferred ? '#e1f1e3' : '#f3f4f6',
                    color: keeperDeferred ? '#358639' : '#374151',
                  }}
                  aria-hidden="true"
                >
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <div className="flex-1 text-left min-w-0">
                  <h3 className="font-bold text-base" style={{ color: '#0f1111' }}>
                    I don't have my V5C to hand
                  </h3>
                  <p className="text-xs mt-1.5 leading-relaxed" style={{ color: '#565959' }}>
                    Tap this to skip vehicle details — you can complete them after payment.
                  </p>
                </div>
                <div
                  className="absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors"
                  style={{
                    border: `2px solid ${keeperDeferred ? '#358639' : '#cbd5e1'}`,
                    backgroundColor: keeperDeferred ? '#358639' : '#ffffff',
                  }}
                  aria-hidden="true"
                >
                  {keeperDeferred && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 13l4 4L19 7"/>
                    </svg>
                  )}
                </div>
              </div>
            </div>
            )}
            <Collapsible text="Need help?" graphic="help" type="green">
              <div className="text-left text-xs grid gap-4 pt-4">
                <p>
                  <b>• I already have a vehicle for {registration}.</b>
                  <br />
                  <i>
                    The Nominee is the name shown as the Registered Keeper of
                    the vehicle.
                  </i>
                </p>
                <p>
                  <b>• I do not have a vehicle for {registration} yet.</b>
                  <br />
                  <i>
                    The Nominee is the name you intend to put as the Registered
                    Keeper of the vehicle.
                  </i>
                </p>
                <p>
                  <b>
                    • I am buying {registration} for a third party or as a gift.
                  </b>
                  <br />
                  <i>
                    This is the name of the recipient so please confirm their
                    Registered Keeper details before proceeding.
                  </i>
                </p>
                <img src={v5cnamearea} alt="" />
              </div>
            </Collapsible>
          </div>
        </div>
        {registrationDetails && (
          <div className="mt-6">
            <p className="text-sm">
              {registrationDetails.vtype == "1" ||
              registrationDetails.vtype == "0" ? (
                <>
                  Please note: This registration mark can be assigned to a{" "}
                  <b>car, van, heavy goods vehicle, moped or motorcycle</b> of
                  any age.
                </>
              ) : (
                <>
                  Please note: This registration mark can only be assigned to a
                  car, van, heavy goods vehicle, moped or motorcycle first
                  manufactured on or after{" "}
                  <b>
                    {registrationDetails.display_date1}. Unfortunately, it
                    cannot be assigned to an older vehicle.
                  </b>
                  &nbsp; If you do not have a vehicle, you may hold the number
                  on the DVLA certificate provided for up to 10 years, or longer for
                  an additional fee.
                </>
              )}
            </p>
          </div>
        )}
        {existDeliveryAndTransfer && (
          <>
            <div>
              <h2 className="font-bold text-xl mt-8 mb-3 text-left">
                Select Delivery Type
                <p className="text-left text-sm">
                  All options are actioned the same day paperwork is received by New Reg
                </p>
              </h2>
              <CheckoutCardRadios
                setFieldValue={setFieldValue}
                selectValue={values.delivery_type}
                radios={deliveryRadios}
                name="delivery_type"
                errorMsg={
                  hadTryToSubmit.current && errors.delivery_type
                    ? errors.delivery_type
                    : ""
                }
              />
            </div>
            <div>
              <h2 className="font-bold text-xl mt-8 mb-3 text-left">
                Select a Transfer Option
              </h2>
              {offerConfig?.transfer_policy === 'forced' && (
                <div className="mb-3 bg-blue-50 border border-blue-200 rounded p-3 text-sm text-left">
                  <p className="font-semibold text-blue-700">✓ Premium Transfer Service is required for this offer.</p>
                  <p className="text-blue-600 text-xs mt-1">This cannot be changed with the current discount applied.</p>
                </div>
              )}
              <div style={offerConfig?.transfer_policy === 'forced' ? { pointerEvents: 'none', opacity: 0.75 } : {}}>
                <CheckoutCardRadios
                  setFieldValue={setFieldValue}
                  selectValue={values.transfer_type}
                  radios={transferRadios}
                  name="transfer_type"
                  errorMsg={
                    hadTryToSubmit.current && errors.transfer_type
                      ? errors.transfer_type
                      : ""
                  }
                />
              </div>
            </div>
          </>
        )}
        {salesFormMessage && <p className="text-sm mb-2">{salesFormMessage}</p>}
        <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mt-8 mb-4">
          <div className="bg-card-bg-grey p-5 rounded-2xl flex flex-col h-full">
            <p className="text-left text-sm font-bold mb-4" style={{ color: "#0f1111" }}>
              Do you authorise the release or qualification of information to a third party calling on your behalf?
            </p>
            <p className="text-left text-xs mb-4" style={{ color: "#565959" }}>
              An example could be your partner or family member calling to track your order progress.
            </p>
            <div className="mt-auto">
              <div className="grid grid-cols-2 gap-3">
                <button type="button" className="h-12 rounded-lg font-semibold text-sm transition-all"
                  style={{ backgroundColor: thirdPartyQualify ? "#358639" : "#ffffff", color: thirdPartyQualify ? "#ffffff" : "#374151", border: thirdPartyQualify ? "2px solid #358639" : "1px solid #d1d5db", boxShadow: thirdPartyQualify ? "0 2px 6px rgba(53,134,57,0.18)" : "none" }}
                  onClick={() => setThirdPartyQualify(true)}>Yes</button>
                <button type="button" className="h-12 rounded-lg font-semibold text-sm transition-all"
                  style={{ backgroundColor: !thirdPartyQualify ? "#358639" : "#ffffff", color: !thirdPartyQualify ? "#ffffff" : "#374151", border: !thirdPartyQualify ? "2px solid #358639" : "1px solid #d1d5db", boxShadow: !thirdPartyQualify ? "0 2px 6px rgba(53,134,57,0.18)" : "none" }}
                  onClick={() => setThirdPartyQualify(false)}>No</button>
              </div>
              <div className="mt-3 min-h-[3rem]">
                <p className="text-xs italic" style={{ color: "#6b7280" }}>The Data Protection Act is here to ensure that personal information is handled fairly and securely.</p>
              </div>
            </div>
          </div>
          <div className="bg-card-bg-grey p-5 rounded-2xl flex flex-col h-full">
            <p className="text-left text-sm font-bold mb-4" style={{ color: "#0f1111" }}>Would you like us to send your communication in plain envelopes?</p>
            <p className="text-left text-xs mb-4" style={{ color: "#565959" }}>Perfect to keep a gift a surprise.</p>
            <div className="mt-auto">
              <div className="grid grid-cols-2 gap-3">
                <button type="button" className="h-12 rounded-lg font-semibold text-sm transition-all"
                  style={{ backgroundColor: sendCommunciation ? "#358639" : "#ffffff", color: sendCommunciation ? "#ffffff" : "#374151", border: sendCommunciation ? "2px solid #358639" : "1px solid #d1d5db", boxShadow: sendCommunciation ? "0 2px 6px rgba(53,134,57,0.18)" : "none" }}
                  onClick={() => setSendCommunciation(true)}>Yes</button>
                <button type="button" className="h-12 rounded-lg font-semibold text-sm transition-all"
                  style={{ backgroundColor: !sendCommunciation ? "#358639" : "#ffffff", color: !sendCommunciation ? "#ffffff" : "#374151", border: !sendCommunciation ? "2px solid #358639" : "1px solid #d1d5db", boxShadow: !sendCommunciation ? "0 2px 6px rgba(53,134,57,0.18)" : "none" }}
                  onClick={() => setSendCommunciation(false)}>No</button>
              </div>
              <div className="mt-3 min-h-[3rem]"></div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 w-full mt-8">
          <ButtonRound
            buttonType="button"
            text="Go back"
            type="black"
            className="h-[60px] text-lg previous-page-btn"
            onClick={prevPage}
          ></ButtonRound>
          <ButtonRound
            buttonType="button"
            text="Next page"
            type="success"
            className="h-[60px] text-lg submit-customise-btn"
            disabled={isSubmitting}
            onClick={() => {
              hadTryToSubmit.current = true;
              formik.submitForm();
            }}
          ></ButtonRound>
        </div>
      </form>
    </div>
  );
};

export { CheckoutCustomise };
